import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, TextField, Chip, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Collapse, Tooltip, Button,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';

import { dbServersAPI } from '../services/api';
import ResourceChart from '../components/ResourceChart';
import Spark from '../components/Spark';

const NATIVE = { select: { native: true }, inputLabel: { shrink: true } };
const WARN = 80;
const CRITICAL = 90;
const RANGES = { '24h': 'Last 24 hours', '7d': 'Last 7 days', '30d': 'Last 30 days' };
const REGIONS = ['Oregon', 'London', 'Paris', 'Sydney'];
const errText = (err, fallback) => err.response?.data?.detail || fallback;
const level = (v) => (v == null ? 'default' : v >= CRITICAL ? 'error' : v >= WARN ? 'warning' : 'success');
const pct = (v) => (v == null ? '-' : `${v}%`);
const SHOWN_CUSTOMERS = 4;
const TEMPDB = 'H:';                       // the TempDB drive is full by design, so it carries no warning colour

// SQL Server keeps most of the server's memory by design, so memory is shown without a warning colour
function Reading({ item, neutral }) {
  if (!item) return <Typography variant="body2" color="text.secondary">-</Typography>;
  return (
    <Tooltip title={`Average ${pct(item.average)}, highest ${pct(item.peak)}${neutral ? '. SQL Server uses most of the memory it is given, so this is not a warning.' : ''}`}>
      <Chip size="small" variant={neutral ? 'outlined' : 'filled'} color={neutral ? 'default' : level(item.latest)} label={pct(item.latest)} />
    </Tooltip>
  );
}

// SQL Server or SQL Agent: Running is green, anything else is red, and no reading at all is shown as such
function SqlState({ server, kind }) {
  const svc = (server.services || []).find((s) => s.kind === kind);
  if (!svc) return <Chip size="small" variant="outlined" label="No data" />;
  return (
    <Tooltip title={svc.name}>
      <Chip size="small" color={svc.state === 'Running' ? 'success' : 'error'} label={svc.state} />
    </Tooltip>
  );
}

function BackupCell({ backups }) {
  if (!backups) return <Typography variant="body2" color="text.secondary">-</Typography>;
  if (backups.red == null) return <Typography variant="body2">{backups.databases} databases</Typography>;
  if (!backups.red && !backups.orange) return <Chip size="small" color="success" label="All current" />;
  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {backups.red > 0 && <Chip size="small" color="error" label={`${backups.red} red`} />}
      {backups.orange > 0 && <Chip size="small" color="warning" label={`${backups.orange} orange`} />}
    </Box>
  );
}

function ServerRow({ server, timestamps, rangeLabel }) {
  const [open, setOpen] = useState(false);
  const [chart, setChart] = useState(null);
  const show = (title, values, color) => () => setChart({ title, values, color });
  const customers = server.customers || [];
  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={`Show details for ${server.host}`} onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontFamily: 'monospace' }}>{server.host}</Typography>
          {server.paired_with?.length > 0 && <Typography variant="caption" color="text.secondary">Same SQL instance as {server.paired_with.join(', ')}</Typography>}
        </TableCell>
        <TableCell>{server.environment}</TableCell>
        <TableCell><Reading item={server.cpu} /></TableCell>
        <TableCell><Reading item={server.memory} neutral /></TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {server.disks.length === 0 && '-'}
            {server.disks.map((d) => (
              <Tooltip key={d.device} title={`${d.device} average ${pct(d.average)}, highest ${pct(d.peak)}${d.device === TEMPDB ? '. The TempDB drive, full by design.' : ''}`}>
                {d.device === TEMPDB
                  ? <Chip size="small" variant="outlined" label={`${d.device} TempDB ${pct(d.latest)}`} />
                  : <Chip size="small" color={level(d.latest)} label={`${d.device} ${pct(d.latest)}`} />}
              </Tooltip>
            ))}
          </Box>
        </TableCell>
        <TableCell><SqlState server={server} kind="sql" /></TableCell>
        <TableCell><SqlState server={server} kind="agent" /></TableCell>
        <TableCell><BackupCell backups={server.backups} /></TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {customers.length === 0 && '-'}
            {customers.slice(0, SHOWN_CUSTOMERS).map((c) => <Chip key={c.code} size="small" variant="outlined" label={c.code} />)}
            {customers.length > SHOWN_CUSTOMERS && <Chip size="small" label={`+${customers.length - SHOWN_CUSTOMERS}`} onClick={() => setOpen(true)} />}
          </Box>
        </TableCell>
        <TableCell>{server.reporting ? <Chip size="small" variant="outlined" color="success" label="Reporting" /> : <Chip size="small" color="warning" label="Not reporting" />}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2, py: 2 }}>
              {server.cpu && <Spark title="CPU used" values={server.cpu.series} timestamps={timestamps} color="#1976d2" onOpen={show('CPU used', server.cpu.series, '#1976d2')} />}
              {server.memory && <Spark title="Memory used" values={server.memory.series} timestamps={timestamps} color="#7b1fa2" onOpen={show('Memory used', server.memory.series, '#7b1fa2')} />}
              {server.disks.map((d) => <Spark key={d.device} title={`Disk ${d.device} used`} values={d.series} timestamps={timestamps} color="#2e7d32" onOpen={show(`Disk ${d.device} used`, d.series, '#2e7d32')} />)}
            </Box>
            <Box sx={{ pb: 2 }}>
              <Typography variant="subtitle2">Customers with databases on this server</Typography>
              {customers.length === 0
                ? <Typography variant="body2" color="text.secondary">None known. This comes from the weekly backup report for the server's SQL instance{server.instance ? ` (${server.instance})` : ''}.</Typography>
                : (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {customers.map((c) => <Chip key={c.code} size="small" variant="outlined" label={`${c.code} (${c.databases} ${c.databases === 1 ? 'database' : 'databases'})`} />)}
                  </Box>
                )}
              {server.backups?.reported_at && <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>From the backup report received {new Date(server.backups.reported_at).toLocaleString()}.</Typography>}
              {server.last_seen && <Typography variant="caption" color="text.secondary" display="block">Last reading {new Date(server.last_seen).toLocaleString()}. Dashed lines mark {WARN}% and {CRITICAL}%. Click a chart to enlarge it.</Typography>}
            </Box>
            {chart && <ResourceChart title={chart.title} subtitle={`${server.host} - ${rangeLabel}`} values={chart.values} timestamps={timestamps} color={chart.color} onClose={() => setChart(null)} />}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function DatabaseServers() {
  const [range, setRange] = useState('24h');
  const [region, setRegion] = useState('');
  const [environment, setEnvironment] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData((await dbServersAPI.list(range)).data); }
    catch (err) { setData(null); setError(errText(err, 'Could not load the database servers')); }
    finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const all = data?.servers || [];
  const environments = useMemo(() => [...new Set(all.map((s) => s.environment))], [all]);
  const shown = all.filter((s) => (!region || s.region === region) && (!environment || s.environment === environment));
  const notRunning = shown.filter((s) => (s.services || []).some((x) => x.state !== 'Running')).length;
  const hot = shown.filter((s) => [s.cpu?.latest, ...s.disks.filter((d) => d.device !== TEMPDB).map((d) => d.latest)].some((v) => v != null && v >= WARN)).length;
  const silent = shown.filter((s) => !s.reporting).length;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Database Servers</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        The shared database servers in each region. CPU, memory, disk and the SQL Server and SQL Agent services come from Sumo Logic; the customers on a server and their backups come from the weekly backup reports. Everything here is read only. A customer's own dedicated database servers are on Server Resources.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField select size="small" label="Region" value={region} onChange={(e) => setRegion(e.target.value)} sx={{ minWidth: 160 }} slotProps={NATIVE}>
          <option value="">All</option>
          {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </TextField>
        <TextField select size="small" label="Environment" value={environment} onChange={(e) => setEnvironment(e.target.value)} sx={{ minWidth: 160 }} slotProps={NATIVE}>
          <option value="">All</option>
          {environments.map((e) => <option key={e} value={e}>{e}</option>)}
        </TextField>
        <ToggleButtonGroup size="small" exclusive value={range} onChange={(e, v) => v && setRange(v)} aria-label="Time range">
          {Object.entries(RANGES).map(([k, label]) => <ToggleButton key={k} value={k}>{label}</ToggleButton>)}
        </ToggleButtonGroup>
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
      </Paper>

      {!loading && data && shown.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`${shown.length} ${shown.length === 1 ? 'server' : 'servers'}`} />
          <Chip color={notRunning ? 'error' : 'success'} variant={notRunning ? 'filled' : 'outlined'} label={notRunning ? `SQL service not running: ${notRunning}` : 'All SQL services running'} />
          <Chip color={hot ? 'warning' : 'success'} variant={hot ? 'filled' : 'outlined'} label={hot ? `At ${WARN}% or more: ${hot}` : `Nothing at ${WARN}% or more`} />
          {silent > 0 && <Chip color="warning" label={`Not reporting: ${silent}`} />}
        </Box>
      )}

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
      {!loading && data && shown.length === 0 && <Alert severity="info">No database servers match.</Alert>}
      {!loading && data && REGIONS.map((name) => {
        const rows = shown.filter((s) => s.region === name);
        if (!rows.length) return null;
        return (
          <Box key={name} sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>{name}</Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell>Server</TableCell>
                    <TableCell>Environment</TableCell>
                    <TableCell>CPU now</TableCell>
                    <TableCell>Memory now</TableCell>
                    <TableCell>Disks now</TableCell>
                    <TableCell>SQL Server</TableCell>
                    <TableCell>SQL Agent</TableCell>
                    <TableCell>Backups</TableCell>
                    <TableCell>Customers</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((s) => <ServerRow key={s.host} server={s} timestamps={data.timestamps} rangeLabel={RANGES[data.range]} />)}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );
      })}
    </Box>
  );
}

export default DatabaseServers;
